import { clsx } from 'clsx';
import React, { RefObject } from 'react';

import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { ILodgingCampgrounds } from '@/services/types/search/lodgingCampgrounds';
import { IVehicle } from '@/services/types/search/vehicle';
import { IVehicleCampgrounds } from '@/services/types/search/vehicleCampgrounds';

import DesktopUniversalSearch from './DesktopUniversalSearch/DesktopUniversalSearch';
import MobileUniversalSearch from './MobileUniversalSearch/MobileUniversalSearch';
import css from './UniversalSearch.module.css';
import {
  EHeaderTab,
  Header_TOP_MENUS_CAMPGROUNDS,
  IHeaderMenu,
} from './UniversalSearchContainer/menu';
import UniversalSearchContainer from './UniversalSearchContainer/UniversalSearchContainer';

interface UniversalSearchProps {
  onSubmit?: () => void;
  handleRecentSearchNavigation?: (url: string) => void;
  location?: { lat: number; lng: number };
  address?: string;
  guests?: IHeaderSearchFilterGuests;
  dates?: IHeaderSearchFilterDates;
  recentSearches?: ILocationSearchAutocompleteListOption[];
  maxRecentSearches: number;
  loading?: boolean;
  showCategory?: boolean;
  showStayFilter?: boolean;
  vehicle?: IVehicle;
  vehicleCampgrounds?: IVehicleCampgrounds;
  lodgingCampgrounds?: ILodgingCampgrounds;
  onChangeAddress?: (address?: string) => void;
  onChangeVehicle?: (vehicle?: IVehicle) => void;
  onChangeVehicleCampgrounds?: (vehicle?: IVehicleCampgrounds) => void;
  onChangeLodgingCampgrounds?: (lodging?: ILodgingCampgrounds) => void;
  onChangeDates: (dates?: IHeaderSearchFilterDates) => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onConfirmAddress?: (address?: string) => void;
  onConfirmDates?: (dates?: IHeaderSearchFilterDates) => void;
  onConfirmGuests?: (guests: IHeaderSearchFilterGuests) => void;
  className?: string;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  openedTab?: IHeaderMenu;
  openHeaderTab: (tab: IHeaderMenu, parentRef: RefObject<HTMLUListElement>) => void;
  hideTabListMenu?: boolean;
  headerTabList: IHeaderMenu[];
  selectedCategory?: IHeaderMenu;
  isGuestOccupancyAvailable?: boolean;
  lockedCategory?: IHeaderMenu;
  isCampground?: boolean;
}

const UniversalSearch: React.FC<UniversalSearchProps> = ({
  onSubmit,
  location,
  address,
  dates,
  guests,
  vehicle,
  vehicleCampgrounds,
  lodgingCampgrounds,
  recentSearches = [],
  maxRecentSearches,
  loading = false,
  onChangeAddress,
  onChangeVehicle,
  onChangeVehicleCampgrounds,
  onChangeLodgingCampgrounds,
  onChangeDates,
  onChangeGuests,
  onConfirmAddress,
  onConfirmDates,
  onConfirmGuests,
  className,
  popularDestinations,
  hideTabListMenu = false,
  headerTabList,
  openedTab,
  openHeaderTab,
  selectedCategory,
  showCategory,
  showStayFilter,
  handleRecentSearchNavigation,
  isGuestOccupancyAvailable,
  isCampground,
}) => {
  const isShowInstantBook = selectedCategory?.tab === 'rv';
  return (
    <div
      className={clsx({
        [css.fullUniversalSearch as string]: showStayFilter,
        [css.withoutTabListMenu as string]: hideTabListMenu,
        [css.withoutCategoryTabs as string]: !showCategory,
      })}>
      {showCategory && (
        <UniversalSearchContainer
          openedTab={openedTab}
          openTab={openHeaderTab}
          secondaryMenu={showStayFilter}
          isShowInstantBook={isShowInstantBook}
          menu={
            selectedCategory?.tab === EHeaderTab.RVS ? headerTabList : Header_TOP_MENUS_CAMPGROUNDS
          }
          className={css.categoryTabs}
        />
      )}
      <div className={`hidden md:block ${className || ''}`}>
        <DesktopUniversalSearch
          location={location}
          address={address}
          dates={dates}
          guests={guests}
          vehicle={vehicle}
          vehicleCampgrounds={vehicleCampgrounds}
          lodgingCampgrounds={lodgingCampgrounds}
          recentSearches={recentSearches}
          maxRecentSearches={maxRecentSearches}
          onSubmit={onSubmit}
          loading={loading}
          onChangeAddress={onChangeAddress}
          onChangeDates={onChangeDates}
          onChangeVehicle={onChangeVehicle}
          onChangeVehicleCampgrounds={onChangeVehicleCampgrounds}
          onChangeLodgingCampgrounds={onChangeLodgingCampgrounds}
          onChangeGuests={onChangeGuests}
          onConfirmAddress={onConfirmAddress}
          onConfirmDates={onConfirmDates}
          onConfirmGuests={onConfirmGuests}
          openedTab={openedTab}
          popularDestinations={popularDestinations}
          isCategoryEnabled={showCategory}
          isStayFilterEnabled={showStayFilter}
          handleRecentSearchNavigation={handleRecentSearchNavigation}
          isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          isCampground={isCampground}
        />
      </div>
      <div className={`w-full md:hidden grid grid-flow-col auto-cols-fr ${className || ''}`}>
        <MobileUniversalSearch
          location={location}
          address={address}
          dates={dates}
          guests={guests}
          vehicle={vehicle}
          vehicleCampgrounds={vehicleCampgrounds}
          lodgingCampgrounds={lodgingCampgrounds}
          recentSearches={recentSearches}
          maxRecentSearches={maxRecentSearches}
          onChangeAddress={onChangeAddress}
          onChangeDates={onChangeDates}
          onChangeVehicle={onChangeVehicle}
          onChangeVehicleCampgrounds={onChangeVehicleCampgrounds}
          onChangeLodgingCampgrounds={onChangeLodgingCampgrounds}
          onChangeGuests={onChangeGuests}
          loading={loading}
          onSubmit={onSubmit}
          openedTab={openedTab}
          popularDestinations={popularDestinations}
          isStayFilterEnabled={showStayFilter}
          handleRecentSearchNavigation={handleRecentSearchNavigation}
          isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          isCampground={isCampground}
        />
      </div>
    </div>
  );
};

export default UniversalSearch;
